/**
 * @module role
 * @description 角色與權限相關型別定義
 */
export { PagePath, SIDEBAR_DEFINITION } from './page-catalog.js';
/** 伺服器擁有的內建角色識別（權限依此判斷，不依角色名稱） @v2.7 R-06 */
export var RoleKey;
(function (RoleKey) {
    RoleKey["SUPER_ADMIN"] = "SUPER_ADMIN";
})(RoleKey || (RoleKey = {}));
/** 操作 Action 常量 */
export var ActionKey;
(function (ActionKey) {
    // 使用者管理
    ActionKey["USER_CREATE"] = "user:create";
    ActionKey["USER_UPDATE"] = "user:update";
    ActionKey["USER_DELETE"] = "user:delete";
    ActionKey["USER_RESET_PASSWORD"] = "user:reset_password";
    ActionKey["USER_MANAGE_2FA"] = "user:manage_2fa";
    /** 授予/撤銷「全館」場館範圍（與一般 user:update 分離的高風險操作） @v2.6 */
    ActionKey["USER_GRANT_VENUE_SCOPE"] = "user:grant_venue_scope";
    ActionKey["DEVICE_BINDING_MANAGE"] = "device_binding:manage";
    // 角色管理
    ActionKey["ROLE_CREATE"] = "role:create";
    ActionKey["ROLE_UPDATE"] = "role:update";
    ActionKey["ROLE_DELETE"] = "role:delete";
    ActionKey["ROLE_ASSIGN"] = "role:assign";
    // 錦標賽
    ActionKey["TOURNAMENT_CREATE"] = "tournament:create";
    ActionKey["TOURNAMENT_UPDATE"] = "tournament:update";
    ActionKey["TOURNAMENT_DELETE"] = "tournament:delete";
    ActionKey["TOURNAMENT_START"] = "tournament:start";
    ActionKey["TOURNAMENT_PAUSE"] = "tournament:pause";
    ActionKey["TIMER_CONTROL"] = "timer:control";
    ActionKey["PLAYER_ELIMINATE"] = "player:eliminate";
    ActionKey["PLAYER_REBUY"] = "player:rebuy";
    ActionKey["PLAYER_ADDON"] = "player:addon";
    ActionKey["TOURNAMENT_UNDO"] = "tournament:undo";
    ActionKey["TOURNAMENT_EMERGENCY"] = "tournament:emergency";
    ActionKey["TOURNAMENT_MANUAL_FIX"] = "tournament:manual_fix";
    ActionKey["TOURNAMENT_REOPEN"] = "tournament:reopen";
    // 會員
    ActionKey["MEMBER_READ"] = "member:read";
    ActionKey["MEMBER_NOTIFICATIONS_MANAGE"] = "member:notifications_manage";
    ActionKey["MEMBER_NOTIFICATIONS_SEND"] = "member:notifications_send";
    ActionKey["RESERVATION_READ"] = "reservation:read";
    ActionKey["RESERVATION_MANAGE"] = "reservation:manage";
    ActionKey["RESERVATION_CHECKIN"] = "reservation:checkin";
    ActionKey["MEMBER_TAGS_MANAGE"] = "member:tags_manage";
    ActionKey["MEMBER_TAGS_ASSIGN"] = "member:tags_assign";
    ActionKey["MEMBER_TAGS_ASSIGN_GLOBAL"] = "member:tags_assign_global";
    ActionKey["MEMBER_TAGS_IMPORT"] = "member:tags_import";
    ActionKey["MEMBER_TAGS_EXPORT"] = "member:tags_export";
    ActionKey["MEMBER_PORTAL_MANAGE"] = "member:portal_manage";
    ActionKey["MEMBER_CREATE"] = "member:create";
    ActionKey["MEMBER_UPDATE"] = "member:update";
    ActionKey["MEMBER_DELETE"] = "member:delete";
    ActionKey["MEMBER_CHECKIN"] = "member:checkin";
    // 盲注結構
    ActionKey["BLIND_STRUCTURE_CREATE"] = "blind_structure:create";
    ActionKey["BLIND_STRUCTURE_UPDATE"] = "blind_structure:update";
    ActionKey["BLIND_STRUCTURE_DELETE"] = "blind_structure:delete";
    // 賽事模板
    ActionKey["TEMPLATE_CREATE"] = "template:create";
    ActionKey["TEMPLATE_UPDATE"] = "template:update";
    ActionKey["TEMPLATE_DELETE"] = "template:delete";
    // 賽事狀態（補充）
    ActionKey["TOURNAMENT_CANCEL"] = "tournament:cancel";
    ActionKey["TOURNAMENT_COMPLETE"] = "tournament:complete";
    ActionKey["TOURNAMENT_RESUME"] = "tournament:resume";
    // 會員進階
    ActionKey["MEMBER_LEVEL_MANAGE"] = "member:level_manage";
    ActionKey["MEMBER_FIELD_MANAGE"] = "member:field_manage";
    ActionKey["MEMBER_BULK_IMPORT"] = "member:bulk_import";
    ActionKey["MEMBER_ADJUST_POINTS"] = "member:adjust_points";
    /** 檢視完整身分證字號（每次檢視皆寫入稽核） @v2.8 */
    ActionKey["MEMBER_VIEW_NATIONAL_ID"] = "member:view_national_id";
    /** 堂票 / 票券 / 月友：作廢、延期、補登、補發、管理種類與月友設定（主管）@phase2A */
    ActionKey["MEMBER_WALLET_MANAGE"] = "member:wallet_manage";
    /** Allow non-deposit ZIPA operations while the venue balance is negative. */
    ActionKey["MEMBER_ZIPA_OVERRIDE_NEGATIVE"] = "member:zipa_override_negative";
    ActionKey["MEMBER_ASSET_TYPE_MANAGE"] = "member:asset_type_manage";
    // 帳務
    ActionKey["MEMBER_ZIPA_TRANSFER"] = "member:zipa_transfer";
    ActionKey["MEMBER_CHARGE_READ"] = "member:charge_read";
    ActionKey["MEMBER_CHARGE_CONFIRM"] = "member:charge_confirm";
    ActionKey["MEMBER_CHARGE_REFUND"] = "member:charge_refund";
    ActionKey["ACCOUNTING_READ"] = "accounting:read";
    ActionKey["ACCOUNTING_OPEN"] = "accounting:open";
    ActionKey["ACCOUNTING_CLOSE"] = "accounting:close";
    ActionKey["ACCOUNTING_EDIT"] = "accounting:edit";
    ActionKey["ACCOUNTING_CATEGORY_MANAGE"] = "accounting:category_manage";
    ActionKey["PAYOUT_APPROVE"] = "payout:approve";
    // 電視顯示
    ActionKey["DISPLAY_MANAGE"] = "display:manage";
    ActionKey["DISPLAY_TIMER_LAYOUT"] = "display:timer_layout";
    // 場館
    ActionKey["VENUE_TABLE_MANAGE"] = "venue:table_manage";
    ActionKey["VENUE_SEAT_MANAGE"] = "venue:seat_manage";
    // 品牌
    ActionKey["BRANDING_MANAGE"] = "branding:manage";
    // 術語
    ActionKey["I18N_MANAGE"] = "i18n:manage";
    // 儀表板
    ActionKey["DASHBOARD_VIEW_KPI"] = "dashboard:view_kpi";
    // 系統
    ActionKey["SYSTEM_SETTINGS"] = "system:settings";
    ActionKey["LOG_DELETE"] = "log:delete";
    // 語音 AI（Voice AI）
    ActionKey["VOICE_USE"] = "voice:use";
})(ActionKey || (ActionKey = {}));
export const ACTION_KEY_GROUPS = [
    {
        labelKey: 'actionGroup_user',
        actions: [
            ActionKey.USER_CREATE,
            ActionKey.USER_UPDATE,
            ActionKey.USER_DELETE,
            ActionKey.USER_RESET_PASSWORD,
            ActionKey.USER_MANAGE_2FA,
            ActionKey.USER_GRANT_VENUE_SCOPE,
            ActionKey.DEVICE_BINDING_MANAGE,
        ],
    },
    {
        labelKey: 'actionGroup_role',
        actions: [
            ActionKey.ROLE_CREATE,
            ActionKey.ROLE_UPDATE,
            ActionKey.ROLE_DELETE,
            ActionKey.ROLE_ASSIGN,
        ],
    },
    {
        labelKey: 'actionGroup_tournament',
        actions: [
            ActionKey.TOURNAMENT_CREATE,
            ActionKey.TOURNAMENT_UPDATE,
            ActionKey.TOURNAMENT_DELETE,
            ActionKey.TOURNAMENT_START,
            ActionKey.TOURNAMENT_PAUSE,
            ActionKey.TOURNAMENT_RESUME,
            ActionKey.TOURNAMENT_CANCEL,
            ActionKey.TOURNAMENT_COMPLETE,
            ActionKey.TOURNAMENT_UNDO,
            ActionKey.TOURNAMENT_EMERGENCY,
            ActionKey.TOURNAMENT_MANUAL_FIX,
            ActionKey.TOURNAMENT_REOPEN,
        ],
    },
    {
        labelKey: 'actionGroup_blindStructure',
        actions: [
            ActionKey.BLIND_STRUCTURE_CREATE,
            ActionKey.BLIND_STRUCTURE_UPDATE,
            ActionKey.BLIND_STRUCTURE_DELETE,
        ],
    },
    {
        labelKey: 'actionGroup_template',
        actions: [ActionKey.TEMPLATE_CREATE, ActionKey.TEMPLATE_UPDATE, ActionKey.TEMPLATE_DELETE],
    },
    {
        labelKey: 'actionGroup_timer',
        actions: [ActionKey.TIMER_CONTROL],
    },
    {
        labelKey: 'actionGroup_player',
        actions: [ActionKey.PLAYER_ELIMINATE, ActionKey.PLAYER_REBUY, ActionKey.PLAYER_ADDON],
    },
    {
        labelKey: 'actionGroup_member',
        actions: [
            ActionKey.MEMBER_READ,
            ActionKey.MEMBER_NOTIFICATIONS_MANAGE,
            ActionKey.MEMBER_NOTIFICATIONS_SEND,
            ActionKey.RESERVATION_READ,
            ActionKey.RESERVATION_MANAGE,
            ActionKey.RESERVATION_CHECKIN,
            ActionKey.MEMBER_TAGS_MANAGE,
            ActionKey.MEMBER_TAGS_ASSIGN,
            ActionKey.MEMBER_TAGS_ASSIGN_GLOBAL,
            ActionKey.MEMBER_TAGS_IMPORT,
            ActionKey.MEMBER_TAGS_EXPORT,
            ActionKey.MEMBER_PORTAL_MANAGE,
            ActionKey.MEMBER_CREATE,
            ActionKey.MEMBER_UPDATE,
            ActionKey.MEMBER_DELETE,
            ActionKey.MEMBER_CHECKIN,
            ActionKey.MEMBER_LEVEL_MANAGE,
            ActionKey.MEMBER_FIELD_MANAGE,
            ActionKey.MEMBER_BULK_IMPORT,
            ActionKey.MEMBER_ADJUST_POINTS,
            ActionKey.MEMBER_VIEW_NATIONAL_ID,
            ActionKey.MEMBER_WALLET_MANAGE,
            ActionKey.MEMBER_ZIPA_OVERRIDE_NEGATIVE,
            ActionKey.MEMBER_ASSET_TYPE_MANAGE,
        ],
    },
    {
        labelKey: 'actionGroup_accounting',
        actions: [
            ActionKey.MEMBER_ZIPA_TRANSFER,
            ActionKey.MEMBER_CHARGE_READ,
            ActionKey.MEMBER_CHARGE_CONFIRM,
            ActionKey.MEMBER_CHARGE_REFUND,
            ActionKey.ACCOUNTING_READ,
            ActionKey.ACCOUNTING_OPEN,
            ActionKey.ACCOUNTING_CLOSE,
            ActionKey.ACCOUNTING_EDIT,
            ActionKey.ACCOUNTING_CATEGORY_MANAGE,
            ActionKey.PAYOUT_APPROVE,
        ],
    },
    {
        labelKey: 'actionGroup_display',
        actions: [ActionKey.DISPLAY_MANAGE, ActionKey.DISPLAY_TIMER_LAYOUT],
    },
    {
        labelKey: 'actionGroup_venue',
        actions: [ActionKey.VENUE_TABLE_MANAGE, ActionKey.VENUE_SEAT_MANAGE],
    },
    {
        labelKey: 'actionGroup_branding',
        actions: [ActionKey.BRANDING_MANAGE],
    },
    {
        labelKey: 'actionGroup_i18n',
        actions: [ActionKey.I18N_MANAGE],
    },
    {
        labelKey: 'actionGroup_dashboard',
        actions: [ActionKey.DASHBOARD_VIEW_KPI],
    },
    {
        labelKey: 'actionGroup_system',
        actions: [ActionKey.SYSTEM_SETTINGS, ActionKey.LOG_DELETE],
    },
    {
        labelKey: 'actionGroup_voice',
        actions: [ActionKey.VOICE_USE],
    },
];
//# sourceMappingURL=role.js.map